import {
  formatRecipientLabel,
  normalizeRecipientOption,
  uniqueRecipientsByKey,
} from '@/composables/people/recipientOptions';
import collaboratorsService from '@/services/collaborators';
import peopleService from '@/services/people';
import paymentReceiptsService from '@/services/paymentReceipts';
import { SEND_EMAIL_TEXT } from '@/texts/catalog/sendEmail';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { resolveText } from '@/utils/textValue';

const uniqueById = (items = []) => {
  const seen = new Set();
  const uniqueItems = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = resolveText(item?.id || item?.document_id || '', '');
    if (id === '' || seen.has(id)) {
      return;
    }

    seen.add(id);
    uniqueItems.push(item);
  });

  return uniqueItems;
};

const resolvePaymentReceiptText = () => {
  const section = SEND_EMAIL_TEXT?.paymentReceipts;
  return section && typeof section === 'object' ? section : {};
};

const normalizeRecipientsCollection = (collection = [], prefix = 'recipient') => {
  const normalized = (Array.isArray(collection) ? collection : [])
    .map((item, index) => normalizeRecipientOption(item, index, prefix))
    .filter(Boolean);

  return uniqueRecipientsByKey(normalized);
};

const resolvePaymentReceiptContextData = (vm) => {
  const contextData = vm.contextData && typeof vm.contextData === 'object' ? vm.contextData : {};
  const paymentReceiptData = contextData.paymentReceiptData && typeof contextData.paymentReceiptData === 'object'
    ? contextData.paymentReceiptData
    : {};

  const paymentReceiptId = Number(contextData.id || contextData.paymentReceiptId || paymentReceiptData.id || 0) || 0;
  const paymentReference = resolveText(
    paymentReceiptData.folio || paymentReceiptData.code || contextData.folio,
    paymentReceiptId > 0 ? `#${paymentReceiptId}` : ''
  );

  const customerName = resolveText(
    contextData.customerName
      || paymentReceiptData.customer_name
      || paymentReceiptData.customerName
      || paymentReceiptData.receiver_business_name,
    ''
  );

  const total = Number(contextData.total ?? paymentReceiptData.total ?? paymentReceiptData.amount ?? 0) || 0;
  const currency = resolveText(contextData.currency || paymentReceiptData.currency, 'MXN');

  return {
    paymentReceiptId,
    paymentReceiptData,
    paymentReference,
    customerName,
    total,
    currency,
  };
};

const resolvePaymentReceiptOwnerId = (paymentContext = {}) => {
  const paymentReceiptData = paymentContext.paymentReceiptData && typeof paymentContext.paymentReceiptData === 'object'
    ? paymentContext.paymentReceiptData
    : {};

  return Number(
    paymentReceiptData.customer_id
      || paymentReceiptData.company_id
      || 0
  ) || 0;
};

const resolvePaymentReceiptPrimaryRecipient = (paymentContext = {}) => {
  const paymentReceiptData = paymentContext.paymentReceiptData && typeof paymentContext.paymentReceiptData === 'object'
    ? paymentContext.paymentReceiptData
    : {};

  const name = resolveText(
    paymentReceiptData.customer_name
      || paymentReceiptData.receiver_business_name
      || paymentContext.customerName,
    ''
  );

  const email = resolveText(
    paymentReceiptData.customer_email
      || paymentReceiptData.email,
    ''
  );

  return {
    id: resolveText(paymentReceiptData.customer_id || paymentReceiptData.company_id || '', ''),
    name,
    email,
    label: formatRecipientLabel(name, email),
  };
};

const resolvePaymentReceiptFallbackRecipients = (paymentContext = {}, primaryRecipient = {}) => {
  const candidates = [];

  if (primaryRecipient && typeof primaryRecipient === 'object') {
    candidates.push(primaryRecipient);
  }

  const paymentReceiptData = paymentContext.paymentReceiptData && typeof paymentContext.paymentReceiptData === 'object'
    ? paymentContext.paymentReceiptData
    : {};

  [
    'persons',
    'people',
    'contacts',
    'contact_people',
    'copyRecipients',
    'ccRecipients',
    'sendCcOptions',
  ].forEach((key) => {
    const list = Array.isArray(paymentReceiptData[key]) ? paymentReceiptData[key] : [];
    list.forEach((item) => {
      candidates.push(item);
    });
  });

  return normalizeRecipientsCollection(candidates, 'fallback-recipient');
};

const buildPaymentReceiptSummaryMeta = (paymentContext = {}) => {
  const summaryText = resolvePaymentReceiptText()?.summary || {};
  const sendTypeLabelValue = resolveText(summaryText?.sendTypeValue, 'Pago');
  const reference = resolveText(paymentContext.paymentReference, '');
  const sendTypeValue = `${sendTypeLabelValue}${reference !== '' ? ` ${reference}` : ''}`.trim();

  return {
    sendTypeLabel: resolveText(summaryText?.sendTypeLabel, ''),
    sendTypeValue,
    counterpartLabel: resolveText(summaryText?.customerLabel || summaryText?.counterpartLabel, ''),
    counterpartValue: resolveText(paymentContext.customerName, ''),
    totalLabel: resolveText(summaryText?.totalLabel, ''),
    totalValue: formatCurrency(paymentContext.total, { currency: paymentContext.currency || 'MXN' }),
  };
};

const resolveInitialToRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedToRecipientOptions) ? vm.normalizedToRecipientOptions : [];

  const preferredIds = options
    .filter((item) => {
      const invoiceMailing = Number(item?.invoice_mailing || 0) === 1;
      const paymentsMailing = Number(item?.payments_mailing || 0) === 1;
      return (invoiceMailing || paymentsMailing) && resolveText(item?.email, '') !== '';
    })
    .map((item) => item.id);

  if (preferredIds.length > 0) {
    return preferredIds;
  }

  const primaryId = resolveText(vm.normalizedPrimaryRecipient?.id, '');
  if (primaryId !== '') {
    return [primaryId];
  }

  const firstRecipient = options.find((item) => resolveText(item?.email, '') !== '');
  return firstRecipient ? [firstRecipient.id] : [];
};

const resolveInitialCcRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedCcRecipientOptions) ? vm.normalizedCcRecipientOptions : [];

  return options
    .filter((item) => {
      const invoiceMailing = Number(item?.invoice_mailing || 0) === 1;
      const paymentsMailing = Number(item?.payments_mailing || 0) === 1;
      return (invoiceMailing || paymentsMailing) && resolveText(item?.email, '') !== '';
    })
    .map((item) => item.id);
};

const resolveDefaultMessage = (customerName = '') => {
  const defaultMessage = resolvePaymentReceiptText()?.defaultMessage;
  if (typeof defaultMessage === 'function') {
    return String(defaultMessage(customerName || '') || '');
  }

  return resolveText(defaultMessage, '');
};

const hydratePaymentReceiptContextData = async (vm) => {
  const paymentContext = resolvePaymentReceiptContextData(vm);
  const ownerId = resolvePaymentReceiptOwnerId(paymentContext);
  const primaryRecipient = resolvePaymentReceiptPrimaryRecipient(paymentContext);

  vm.text = resolvePaymentReceiptText();
  vm.summaryMeta = buildPaymentReceiptSummaryMeta(paymentContext);
  vm.primaryRecipient = primaryRecipient;
  vm.messageType = 'payment_receipt';
  vm.messageTypeCandidates = ['payment_receipt'];

  const customerName = resolveText(paymentContext.customerName, '').replace(/\s+/g, ' ').trim();
  vm.messageVariables = {
    customer: customerName,
    customerName,
    NOMBRE_CLIENTE: customerName,
  };
  vm.messageLoadingText = resolveText(vm.text?.messageLoadingText, '');
  vm.initialMessage = resolveDefaultMessage(customerName);
  vm.recipientsLoading = ownerId > 0;

  const [recipientsResult, copyResult, documentsResult] = await Promise.allSettled([
    ownerId > 0
      ? peopleService.selectors({ customer_id: ownerId, mailing_type: 'payments', only_enabled: 1 })
      : Promise.resolve([]),
    collaboratorsService.invoiceMailingSelectors(),
    paymentContext.paymentReceiptId > 0
      ? paymentReceiptsService.documentsIndex(paymentContext.paymentReceiptId)
      : Promise.resolve({}),
  ]);

  let recipientOptions = [];
  if (recipientsResult.status === 'fulfilled') {
    recipientOptions = normalizeRecipientsCollection(extractCollection(recipientsResult.value), 'person');
  }

  if (recipientOptions.length === 0) {
    recipientOptions = resolvePaymentReceiptFallbackRecipients(paymentContext, primaryRecipient);
  }

  let copyOptions = [];
  if (copyResult.status === 'fulfilled') {
    copyOptions = normalizeRecipientsCollection(extractCollection(copyResult.value), 'collaborator');
  }

  let serviceDocuments = [];
  if (documentsResult.status === 'fulfilled') {
    const payload = extractPayload(documentsResult.value);
    serviceDocuments = Array.isArray(payload?.documents) ? payload.documents : [];
  }

  vm.recipientOptions = recipientOptions;
  vm.copyOptions = copyOptions;
  vm.invoiceAttachmentOptions = [];
  vm.documentAttachmentOptions = uniqueById(serviceDocuments);
  vm.recipientsLoading = false;
  vm.initialToRecipientIds = resolveInitialToRecipientIds(vm);
  vm.initialCcRecipientIds = resolveInitialCcRecipientIds(vm);
};

export const sendEmailPaymentReceiptContextConfig = Object.freeze({
  supportsInvoiceAttachments: true,
  allowInvoiceAttachmentSelection: false,
  resolveText() {
    return resolvePaymentReceiptText();
  },
  hydrateContextData: hydratePaymentReceiptContextData,
  resolveSelectedInvoiceAttachmentItems() {
    return [];
  },
  resolveDefaultInvoiceAttachmentItem(vm) {
    const summaryValue = resolveText(vm.summaryData?.sendTypeValue, 'Pago');
    const isStamped = Number(vm?.contextData?.paymentReceiptData?.is_stamped || 0) === 1;
    const labelSuffix = isStamped ? '(PDF/XML)' : '(PDF)';

    return {
      id: 'payment-receipt-default-files',
      label: `${summaryValue} ${labelSuffix}`.trim(),
      group: 'invoice-default',
      locked: true,
    };
  },
  resolveInvoiceAttachmentSummaryLabel(vm, option) {
    return String(option?.label || '').trim();
  },
});
